/**
 * rtl-slider-fix.js
 *
 * Fixes for RTL (Arabic) product gallery slider and thumbnail strip on mobile.
 * Only executes when <html dir="rtl"> is present — zero impact on LTR/English.
 *
 * Root issues addressed:
 *  1. SliderComponent.initPages() — sliderItemOffset is negative in RTL flex layouts
 *     because items[0] has the largest offsetLeft (rightmost) and items[1] is smaller.
 *  2. SliderComponent.update() — page calculation breaks with negative offset.
 *  3. SliderComponent.onButtonClick() — "next" button does nothing in Chrome/Safari RTL
 *     because scrollLeft is non-negative (0 = right/start) and adding a negative offset
 *     clamps to 0.
 *  4. ProductGallery.goToFirstSlide() — scrollTo({left:0}) shows the RIGHTMOST content
 *     in Chrome/Safari RTL (which is correct for Chrome), but Firefox uses the opposite
 *     scrollLeft polarity. Using scrollIntoView with inline:'start' is cross-browser.
 *  5. Thumbnail strip scroll — after a gang-variant change, active thumbnails are
 *     prepended (go to right in RTL flex). The strip must scroll to show them.
 *
 * scrollLeft polarity in RTL (varies by browser):
 *  Chrome/Safari ≥ 2020 : scrollLeft = 0 at RIGHT (natural RTL start), increases going left
 *  Firefox              : scrollLeft = maxScroll at RIGHT, decreases going left
 *  We detect which model is in use at runtime and adapt accordingly.
 */
(function () {
  'use strict';

  // Bail out immediately on LTR pages.
  if (document.documentElement.dir !== 'rtl') return;

  /* ── Browser RTL scroll-polarity detection ──────────────────────────────
     Returns true  if scrollLeft at the RIGHT (natural RTL start) is the MAXIMUM
     positive value (Firefox model).
     Returns false if scrollLeft is 0 at the RIGHT (Chrome/Safari model).        */
  function detectFirefoxRTLPolarity() {
    var probe = document.createElement('div');
    probe.style.cssText =
      'direction:rtl;overflow:auto;width:1px;height:1px;' +
      'position:fixed;top:-9999px;left:-9999px;visibility:hidden';
    probe.innerHTML = '<div style="width:2px;height:1px"></div>';
    document.body.appendChild(probe);
    var result = probe.scrollLeft > 0; // Firefox: max (=1) here; Chrome: 0
    document.body.removeChild(probe);
    return result;
  }

  /* ── Main patch function ─────────────────────────────────────────────── */
  function applyRTLSliderFixes() {
    var ffPolarity = detectFirefoxRTLPolarity();

    /* Helper: convert browser-specific RTL scrollLeft → a normalised value
       where 0 = natural start (rightmost content, first item in RTL).          */
    function normalisedScrollLeft(el) {
      if (ffPolarity) {
        // Firefox: scrollLeft is large at start, small (0) at end
        return (el.scrollWidth - el.clientWidth) - el.scrollLeft;
      }
      // Chrome/Safari: scrollLeft is 0 at start, large at end — already normalised
      return el.scrollLeft;
    }

    /* Helper: convert a normalised scroll value back to browser's scrollLeft   */
    function denormalisedScrollLeft(el, normalised) {
      if (ffPolarity) {
        return (el.scrollWidth - el.clientWidth) - normalised;
      }
      return normalised;
    }

    // ── Patch SliderComponent ─────────────────────────────────────────────
    var SliderClass = customElements.get('slider-component');
    if (!SliderClass) return;

    /**
     * initPages — use absolute value of sliderItemOffset so that
     * slidesPerPage and totalPages are always positive.
     */
    SliderClass.prototype.initPages = function () {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(function (el) {
        return el.clientWidth > 0;
      });
      if (this.sliderItemsToShow.length < 2) return;

      // In RTL flex the second item has a SMALLER offsetLeft than the first
      // (items flow right-to-left), so this value is negative.
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      var absOffset = Math.abs(this.sliderItemOffset) || 1;
      this.slidesPerPage = Math.floor(this.slider.clientWidth / absOffset);
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    };

    /**
     * update — derive currentPage from the normalised (direction-agnostic)
     * scroll position and use absolute offset for the division.
     */
    SliderClass.prototype.update = function () {
      var previousPage = this.currentPage;
      var absOffset = Math.abs(this.sliderItemOffset) || 1;
      var norm = normalisedScrollLeft(this.slider);

      this.currentPage = Math.round(norm / absOffset) + 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.currentPage !== previousPage) {
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1]
            }
          })
        );
      }

      if (this.currentPage === 1 && this.currentPage === this.totalPages) {
        this.classList.add('slider--no-buttons');
      } else {
        this.classList.remove('slider--no-buttons');
      }

      if (this.enableSliderLooping) return;

      var isFirstSlide = this.currentPage === 1;
      var isLastSlide  = this.currentPage === this.sliderItemsToShow.length;

      if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      var last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
      if (isLastSlide || this.isSlideVisible(last)) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    };

    /**
     * isSlideVisible — check physical visibility using the RTL-corrected
     * viewport bounds.
     *
     * In RTL flex: item1 (DOM-first, rightmost) has the largest offsetLeft.
     * Visible physical range when normalised scroll = N and absOffset = W:
     *   visibleLeft  = items[0].offsetLeft - N          (items[0] is rightmost)
     *   visibleRight = items[0].offsetLeft - N + clientWidth
     * Or equivalently, using scrollWidth:
     *   visibleRight = scrollWidth - N
     *   visibleLeft  = scrollWidth - N - clientWidth
     */
    SliderClass.prototype.isSlideVisible = function (element, offset) {
      offset = offset || 0;
      var norm = normalisedScrollLeft(this.slider);
      var visibleRight = this.slider.scrollWidth - norm;
      var visibleLeft  = visibleRight - this.slider.clientWidth;
      return (
        element.offsetLeft >= (visibleLeft - offset) &&
        (element.offsetLeft + element.clientWidth) <= (visibleRight + offset)
      );
    };

    /**
     * onButtonClick — in RTL the "next" button should INCREASE the normalised
     * scroll (scroll further left to show later items) and "prev" should DECREASE
     * it (scroll right to show earlier items).
     */
    SliderClass.prototype.onButtonClick = function (event) {
      event.preventDefault();
      var step      = event.currentTarget.dataset.step || 1;
      var absOffset = Math.abs(this.sliderItemOffset) || 0;
      var norm      = normalisedScrollLeft(this.slider);

      var newNorm;
      if (event.currentTarget.name === 'next') {
        newNorm = norm + step * absOffset;   // scroll further left (toward RTL end)
      } else {
        newNorm = norm - step * absOffset;   // scroll right (toward RTL start)
      }

      this.slider.scrollTo({ left: denormalisedScrollLeft(this.slider, newNorm) });
    };

    // ── Patch ProductGallery.goToFirstSlide ───────────────────────────────
    var ProductGalleryClass = customElements.get('product-gallery');
    if (ProductGalleryClass) {
      /**
       * goToFirstSlide — scroll to make the first slide visible (rightmost
       * in RTL layout).  scrollIntoView with inline:'start' is direction-aware
       * and works consistently across Chrome, Safari and Firefox.
       */
      ProductGalleryClass.prototype.goToFirstSlide = function () {
        var items = this.sliderItemsToShow;
        if (items && items.length) {
          items[0].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        } else {
          // Fallback: scroll to the natural RTL start
          this.slider.scrollTo({
            left: denormalisedScrollLeft(this.slider, 0)
          });
        }
      };
    }

    // ── Thumbnail strip scroll after gang-variant change ──────────────────
    /**
     * When a gang colour variant is selected:
     *  1. All gang items lose gang__active → display:none
     *  2. Pink (new colour) items get gang__active → display:block
     *  3. Active items are prepended to the DOM → appear at the RIGHT in RTL flex
     *  4. thumbnailSlider.resetPages() recalculates offsets
     *
     * BUT the thumbnail strip's scroll position is never explicitly reset.
     * In RTL the newly-prepended (rightmost) thumbnails may be off-screen if
     * the strip was previously scrolled left.
     *
     * Fix: on every variant:change, after two animation-frames (to let the DOM
     * and CSS settle), scroll each thumbnail strip so the first active thumb is
     * in view at the inline start (right in RTL).
     */
    document.addEventListener('variant:change', function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var thumbnailSliders = document.querySelectorAll('thumbnail-slider');
          thumbnailSliders.forEach(function (ts) {
            if (ts.classList.contains('hidden')) return;

            // Find the first gang-active OR just the is-active thumbnail
            var firstActive =
              ts.querySelector('[data-gang-option].gang__active') ||
              ts.querySelector('.thumbnail-list__item.is-active');

            if (firstActive) {
              firstActive.scrollIntoView({
                block: 'nearest',
                inline: 'start'   // 'start' = right side in RTL
              });
            }
          });
        });
      });
    });
  }

  /* ── Entry point ─────────────────────────────────────────────────────── */
  // global.js defines the custom elements with defer, so by the time our
  // deferred script runs they are registered.  If somehow this runs before
  // DOMContentLoaded, wait for it.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyRTLSliderFixes);
  } else {
    applyRTLSliderFixes();
  }
})();
